/**
 * Carousel slide media from a resource record — mirrors the video URL / poster
 * rules used when inserting a single video from TiptapToolbar so slideshow
 * videos get the real file as `src` and the cover image as `poster`.
 */

export function isVideoUrl(urlString) {
  if (!urlString) return false;
  try {
    const urlObj = new URL(urlString);
    const rsct = urlObj.searchParams.get('rsct');
    if (rsct && rsct.startsWith('video/')) return true;
    const decoded = decodeURIComponent(urlString);
    const videoExtensions = /\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv|m4v|3gp)(\?|$|&)/i;
    return videoExtensions.test(urlString) || videoExtensions.test(decoded);
  } catch (e) {
    const videoExtensions = /\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv|m4v|3gp)(\?|$|&)/i;
    if (/rsct=video\//i.test(urlString)) return true;
    return videoExtensions.test(urlString);
  }
}

/**
 * Resolve video `src` and poster URL from a resource (aligned with TiptapToolbar / ResourceModal).
 * `poster` is the cover/thumbnail only when it is a separate URL from the main video `src`.
 */
export function videoSrcAndPosterFromResource(resource) {
  if (!resource || resource.file_type !== 'VIDEO') {
    return { src: null, poster: '' };
  }

  let src = null;
  if (resource.downloadable_file_attachment_url) {
    src = resource.downloadable_file_attachment_url;
  } else if (resource.display_image_file_url && isVideoUrl(resource.display_image_file_url)) {
    src = resource.display_image_file_url;
  }

  let poster = '';
  const display = resource.display_image_file_url;
  if (display && src) {
    if (display === src) {
      poster = '';
    } else if (isVideoUrl(display)) {
      poster = '';
    } else {
      poster = display;
    }
  }

  return { src, poster: poster || '' };
}

/**
 * Build one carousel slide object from a grid resource.
 * @param {object} resource API resource
 * @param {object|null} preservedSlide prior slide matched by id/src (caption preserved)
 */
export function carouselSlideFromResource(resource, preservedSlide = null) {
  if (!resource) return null;

  const isVideo = resource.file_type === 'VIDEO';
  let src = null;
  let poster = '';

  if (isVideo) {
    const vp = videoSrcAndPosterFromResource(resource);
    src = vp.src;
    poster = vp.poster || '';
  } else {
    src =
      resource.downloadable_file_attachment_url ||
      resource.display_image_file_url ||
      null;
  }

  if (!src) return null;

  const resourceId = resource.id != null ? String(resource.id) : undefined;

  const downloadableSignedId = resource.downloadable_file_signed_id || null;
  const displaySignedId = resource.display_image_file_signed_id || null;
  let signedId = downloadableSignedId || null;
  if (!signedId && !isVideo && displaySignedId) {
    signedId = displaySignedId;
  }

  let posterSignedId = null;
  if (isVideo && poster) {
    posterSignedId = displaySignedId;
  }

  const slide = {
    type: isVideo ? 'video' : 'image',
    src,
    poster: isVideo ? poster : '',
    caption: preservedSlide?.caption || '',
    alt: resource.title || '',
    resourceId,
  };
  if (signedId) slide.signedId = String(signedId);
  if (posterSignedId) slide.posterSignedId = String(posterSignedId);
  return slide;
}

/**
 * After updating one slide's media, copy `src` / `poster` / `type` / `alt` to every
 * other slide with the same `resourceId` so duplicate uses of the same video stay in sync.
 */
export function propagateCarouselSlideMediaToSharedResourceIds(slides, referenceSlide) {
  if (!Array.isArray(slides) || !referenceSlide?.resourceId) return slides;
  const rid = String(referenceSlide.resourceId);
  return slides.map((s) => {
    if (String(s?.resourceId) !== rid) return s;
    const next = {
      ...s,
      src: referenceSlide.src,
      poster: referenceSlide.poster != null ? referenceSlide.poster : '',
      type: referenceSlide.type,
      alt: referenceSlide.alt != null ? referenceSlide.alt : s.alt,
    };
    if (referenceSlide.signedId) next.signedId = referenceSlide.signedId;
    if (referenceSlide.posterSignedId) next.posterSignedId = referenceSlide.posterSignedId;
    return next;
  });
}
